import { ImportJob } from '../../../project-corpus/domain/types';

/** One presentational row in the search reproducibility record table. */
export interface SearchRecordRow {
  sourceLabel: string;
  query: string;
  workTypes: string[];
  sourceTypes: string[];
  date: string | null; // ISO-8601, rendered with the date pipe
  imported: number;
  duplicates: number;
  incompleteMetadataRemoved: number;
  undoneRemoved: number;
}

/**
 * Human-readable source label for the search record. Mirrors the backend `cleanSourceName`
 * (PrismaServiceImpl) so the search-record table, the CSV export, and the PRISMA databases breakdown
 * all show the SAME source name. OpenAlex drops the query suffix because the terms have their own
 * column. Non-canonical sources (e.g. the demo's "Scopus.bib") surface as the real database name
 * rather than being coerced to "OpenAlex".
 */
export function cleanSourceLabel(rawSource: string | null | undefined): string {
  if (rawSource == null || rawSource.trim().length === 0) {
    return 'Unknown Database';
  }
  const s = rawSource.trim();
  if (s.toLowerCase().startsWith('query:')) {
    return 'OpenAlex';
  }
  if (s.toLowerCase() === 'bibtex') {
    return 'BibTeX Import';
  }
  if (s.toLowerCase() === 'pdf') {
    return 'PDF Upload';
  }
  if (s.toLowerCase() === 'group_corpus') {
    return 'Group Corpus';
  }
  const cleaned = s.replace(/[_-]/g, ' ');
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}

/** The query terms for an OpenAlex search (the part after "query:"); empty for other sources. */
export function extractQuery(rawSource: string | null | undefined): string {
  if (rawSource != null && rawSource.trim().toLowerCase().startsWith('query:')) {
    return rawSource.trim().substring(6).trim();
  }
  return '';
}

/**
 * The raw backend source for a job. Prefers the preserved `rawSource`; falls back to reconstructing
 * it from the coerced enum for jobs not produced by the backend mapper (in-memory/optimistic). The
 * fallback is exact for canonical sources; the only lossy case (unknown source coerced to OPENALEX)
 * never occurs without a rawSource because it only comes from the backend.
 */
function rawSourceOf(job: ImportJob): string {
  if (job.rawSource != null && job.rawSource.length > 0) {
    return job.rawSource;
  }
  switch (job.source) {
    case 'OPENALEX':
      return 'query:' + job.descriptor;
    case 'BIBTEX':
      return 'BIBTEX';
    case 'PDF':
      return 'PDF';
    case 'GROUP_CORPUS':
      return 'GROUP_CORPUS';
    default:
      return job.descriptor;
  }
}

/**
 * The Source column label for a job: the user-facing import-job title (what the corpus job card
 * shows and what the demo sets to the database name, matching the PRISMA databases breakdown).
 * OpenAlex query jobs collapse to "OpenAlex" because their title embeds the query, which the Query
 * column already carries. Falls back to the cleaned raw source when no title is set.
 */
function sourceLabelFor(job: ImportJob, raw: string): string {
  if (raw.toLowerCase().startsWith('query:')) {
    return 'OpenAlex';
  }
  if (job.title != null && job.title.trim().length > 0) {
    return job.title.trim();
  }
  return cleanSourceLabel(raw);
}

/** Maps a corpus import job to a search-record row, labelling the source like the rest of the UI. */
export function toSearchRecordRow(job: ImportJob): SearchRecordRow {
  const raw = rawSourceOf(job);
  return {
    sourceLabel: sourceLabelFor(job, raw),
    query: extractQuery(raw),
    workTypes: job.workTypes ?? [],
    sourceTypes: job.sourceTypes ?? [],
    date: job.startedAt ?? null,
    imported: job.itemsAdded ?? 0,
    duplicates: job.papersSkipped ?? 0,
    incompleteMetadataRemoved: job.papersIncompleteRemoved ?? 0,
    undoneRemoved: job.papersUndone ?? 0,
  };
}
